import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CAPTCHA_STORAGE_KEYS, MCP_LOGIN_STORAGE_KEY } from "@/config/security";
import { setEncrypted } from "@/services/core/cryptoStorage";
import { isCaptchaValid } from "@/utils/helpers";

export const MathCaptcha = ({
  gpcService,
  mcpConfig,
  onSuccess,
  onSkip,
  onError,
  onClose,
  initialStep = null,
}) => {
  const { t } = useTranslation();
  const [challenge, setChallenge] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [step, setStep] = useState("captcha");
  const [loginFields, setLoginFields] = useState({});

  const requiresMcpLogin =
    mcpConfig?.enabled && mcpConfig?.auth_mode === "credentials";
  const mcpLoginFields = mcpConfig?.login_fields?.length
    ? mcpConfig.login_fields
    : [
        { key: "username", label: "Usuario", type: "text" },
        { key: "password", label: "Contraseña", type: "password" },
      ];

  useEffect(() => {
    if (initialStep === "mcp") {
      setStep("mcp");
      setIsLoading(false);
      return;
    }

    const alreadyVerified = isCaptchaValid();

    if (alreadyVerified) {
      if (onSuccess) onSuccess();
    } else {
      generateChallenge();
    }
  }, []);

  const generateChallenge = async () => {
    setIsLoading(true);
    setError("");
    setUserAnswer("");

    try {
      const response = await gpcService.generateCaptchaChallenge();

      if (response.success && response.challenge_id) {
        setChallenge(response.question);
        setChallengeId(response.challenge_id);
      } else {
        throw new Error("Failed to generate challenge");
      }
    } catch (err) {
      setError(
        t(
          "captcha.errorGenerating",
          "Error generating challenge. Please try again.",
        ),
      );
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const parsedAnswer = parseInt(userAnswer, 10);

      if (isNaN(parsedAnswer)) {
        setError(t("captcha.invalidNumber"));
        setIsSubmitting(false);
        return;
      }

      const response = await gpcService.verifyCaptchaAnswer(
        challengeId,
        parsedAnswer,
      );

      if (response.success) {
        localStorage.setItem(CAPTCHA_STORAGE_KEYS.VERIFIED, "true");
        localStorage.setItem(
          CAPTCHA_STORAGE_KEYS.TIMESTAMP,
          Date.now().toString(),
        );

        if (onSuccess) {
          onSuccess();
        }
      } else {
        const attemptsLeft = response.attempts_remaining;
        setAttemptsRemaining(attemptsLeft);

        if (attemptsLeft !== undefined && attemptsLeft > 0) {
          setError(
            t("captcha.incorrectAnswerWithAttempts", {
              attempts: attemptsLeft,
              defaultValue: `Incorrect answer. {{attempts}} attempts remaining`,
            }),
          );
        } else {
          setError(t("captcha.incorrectAnswer"));
        }

        setUserAnswer("");
        setIsSubmitting(false);
      }
    } catch (err) {
      const errorCode = err.error_code;

      switch (errorCode) {
        case "CHALLENGE_NOT_FOUND":
        case "CHALLENGE_EXPIRED":
          setError(
            t(
              "captcha.challengeExpired",
              "Challenge expired. Generating a new one...",
            ),
          );
          setTimeout(() => generateChallenge(), 2000);
          break;

        case "MAX_ATTEMPTS_EXCEEDED":
          setError(
            t(
              "captcha.maxAttemptsExceeded",
              "Maximum attempts exceeded. Generating a new challenge...",
            ),
          );
          setTimeout(() => generateChallenge(), 2000);
          break;

        case "INVALID_SESSION":
          setError(
            t(
              "captcha.invalidSession",
              "Invalid session. Please refresh the page.",
            ),
          );
          break;

        default:
          setError(t("captcha.error"));
      }

      setIsSubmitting(false);

      if (onError) {
        onError(err);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setUserAnswer(value);
      setError("");
    }
  };

  const handleMcpSubmit = async (e) => {
    e.preventDefault();
    const allFilled = mcpLoginFields.every((f) => loginFields[f.key]?.trim());
    if (!allFilled) {
      setError(t("moodle.requiredFields"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await gpcService.verifyMcpCredentials(loginFields);
      if (response.success) {
        await setEncrypted(MCP_LOGIN_STORAGE_KEY, loginFields);
        if (onSuccess) onSuccess();
      } else {
        setError(
          t(
            "moodle.invalidCredentials",
            "Credenciales incorrectas. Intenta nuevamente.",
          ),
        );
      }
    } catch (err) {
      let parsed = {};
      try {
        parsed = JSON.parse(err.message);
      } catch (_) {}
      if (parsed.errorCode === "INVALID_CREDENTIALS") {
        setError(
          t(
            "moodle.invalidCredentials",
            "Credenciales incorrectas. Intenta nuevamente.",
          ),
        );
      } else if (parsed.errorCode === "NETWORK_ERROR") {
        setError(
          t(
            "moodle.networkError",
            "Error de conexión. Verifica tu red e intenta nuevamente.",
          ),
        );
      } else {
        setError(
          t(
            "moodle.verificationError",
            "Error al verificar. Intenta nuevamente.",
          ),
        );
      }
      if (onError) onError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "mcp") {
    const displayName = mcpConfig?.display_name || t("moodle.title");
    return (
      <div className="gpc-captcha-container">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="gpc-captcha-close-btn"
            aria-label={t("chat.close")}
            title={t("chat.close")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
        <div className="gpc-captcha-content">
          <div className="gpc-captcha-header">
            <svg
              className="gpc-captcha-icon"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <h3 className="gpc-captcha-title">{displayName}</h3>
            <p className="gpc-captcha-subtitle">{t("moodle.subtitle")}</p>
          </div>

          <form onSubmit={handleMcpSubmit} className="gpc-captcha-form">
            <div className="gpc-captcha-fields-scroll">
              {mcpLoginFields.map((field, idx) => (
                <div key={field.key} className="gpc-captcha-question">
                  <label className="gpc-captcha-label">{field.label}</label>
                  <input
                    type={field.type || "text"}
                    value={loginFields[field.key] || ""}
                    onChange={(e) => {
                      setLoginFields((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }));
                      setError("");
                    }}
                    className="gpc-captcha-text-input"
                    autoFocus={idx === 0}
                    autoComplete={
                      field.type === "password" ? "current-password" : field.key
                    }
                  />
                </div>
              ))}
            </div>

            {error && (
              <div className="gpc-captcha-error">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth={2} />
                  <line
                    x1="12"
                    y1="8"
                    x2="12"
                    y2="12"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                  <line
                    x1="12"
                    y1="16"
                    x2="12.01"
                    y2="16"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="gpc-captcha-button"
              disabled={
                isSubmitting ||
                !mcpLoginFields.every((f) => loginFields[f.key]?.trim())
              }
            >
              {isSubmitting ? (
                <>
                  <div className="gpc-spinner gpc-button-spinner"></div>
                  {t("captcha.verifying")}
                </>
              ) : (
                t("moodle.submit")
              )}
            </button>
          </form>

          {onSkip && (
            <button type="button" className="gpc-captcha-skip" onClick={onSkip}>
              {t("moodle.skip")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading || !challenge) {
    return (
      <div className="gpc-captcha-loading">
        <div className="gpc-spinner"></div>
        <p>{t("captcha.loading", "Loading challenge...")}</p>
      </div>
    );
  }

  return (
    <div className="gpc-captcha-container">
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="gpc-captcha-close-btn"
          aria-label={t("chat.close")}
          title={t("chat.close")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}
      <div className="gpc-captcha-content">
        <div className="gpc-captcha-header">
          <svg
            className="gpc-captcha-icon"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <h3 className="gpc-captcha-title">{t("captcha.title")}</h3>
          <p className="gpc-captcha-subtitle">{t("captcha.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="gpc-captcha-form">
          <div className="gpc-captcha-question">
            <label htmlFor="captcha-answer" className="gpc-captcha-label">
              {t("captcha.question")}
            </label>
            <div className="gpc-captcha-challenge">
              <span className="gpc-captcha-math">{challenge}</span>
              <span className="gpc-captcha-equals">=</span>
              <input
                id="captcha-answer"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={userAnswer}
                onChange={handleInputChange}
                placeholder="?"
                className={`gpc-captcha-input ${
                  error ? "gpc-captcha-input-error" : ""
                }`}
                disabled={isSubmitting}
                autoFocus
                autoComplete="off"
              />
            </div>
          </div>

          {error && (
            <div className="gpc-captcha-error">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="12"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <line
                  x1="12"
                  y1="16"
                  x2="12.01"
                  y2="16"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {attemptsRemaining !== null && attemptsRemaining > 0 && (
            <div className="gpc-captcha-attempts">
              {t("captcha.attemptsLeft", {
                count: attemptsRemaining,
                defaultValue: `{{count}} attempts remaining`,
              })}
            </div>
          )}

          <button
            type="submit"
            className="gpc-captcha-button"
            disabled={isSubmitting || !userAnswer}
          >
            {isSubmitting ? (
              <>
                <div className="gpc-spinner gpc-button-spinner"></div>
                {t("captcha.verifying")}
              </>
            ) : (
              t("captcha.verify")
            )}
          </button>
        </form>

        <p className="gpc-captcha-info">{t("captcha.info")}</p>
      </div>
    </div>
  );
};
